/**
 * IntroOverlay — first ~5s of each clip.
 *
 * Per Figma layer note: "On Load: 1 second animate in (player/logo slide in
 * from left, text from right), clip plays, audio fades in, dark overlay,
 * holds 3 seconds, 1 second fade out, audio to full volume. Clip sponsor
 * text fades away, only watermark remains."
 *
 * Layout (left → right):
 *   [PlayerCutout]  [VsPill / BigHeadline / SkillPillsRow]
 *
 * The player image is anchored bottom-left, full overlay height, with a
 * right-side fade-out so it blends into the dark scrim instead of looking
 * like a hard rectangular crop. No card around the headline — copy reads
 * directly on top of the dimmed clip per Figma.
 */
import { useRecoilValue } from "recoil";
import { Box, Flex, Image, Text } from "rebass/styled-components";
import styled, { css, keyframes } from "styled-components";

import { ClipBadges } from "MVVM/Components/ClipBadge";

import {
  BrandedActiveClipState,
  BrandedLayoutVariantState,
  BrandedPlaybackPhaseState,
} from "../../State";
import { buildMockMatchContext, buildMockTClipTags } from "../../mockData";
import {
  getEventLogo,
  getEventName,
  getOpponentTeamLogo,
  getOpponentTeamName,
  getPlayerName,
  getPlayerPhoto,
  getTeamLogo,
  getTeamName,
  getViewCount,
} from "../../metadataHelpers";
import { composeBrandedTitle } from "../../titleComposer";
import { TrendingIcon } from "../NowPlayingHeader/icons";
import { TeamCrest } from "../PlaylistRow/TeamCrest";

/**
 * Per Figma layer note: 1s slide-in → 3s hold → 1s fade-out, total 5s.
 * Wall-clock timed (CSS animations) rather than driven off elapsed video
 * time. Trade-off: if the user pauses during intro, the animation keeps
 * advancing — acceptable for V1 since 5s is short and pause-during-intro
 * is rare.
 *
 * Restart on clip change is handled via a React `key` on the Scrim
 * (clip._id), which forces remount and replays the keyframes from 0%.
 */
const slideInLeft = keyframes`
  from { transform: translateX(-30%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

const slideInRight = keyframes`
  from { transform: translateX(20%); opacity: 0; }
  to   { transform: translateX(0);   opacity: 1; }
`;

const fadeOut = keyframes`
  from { opacity: 1; }
  to   { opacity: 0; }
`;

const scrimSequence = keyframes`
  0%   { opacity: 0; }
  20%  { opacity: 1; }
  80%  { opacity: 1; }
  100% { opacity: 0; }
`;

/* Easing tuned for the slide-in: quick start, soft settle. */
const SLIDE_EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

const Scrim = styled(Box)<{ $visible: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 2;
  /* Never intercept input, even while visible. The scrim is purely
     informational (no CTAs), and its visual fade-out is wall-clock CSS
     animation while $visible tracks video time — the two drift under
     buffering/autoplay delay, which used to leave an invisible scrim
     eating taps: tap-to-pause did nothing and the hidden tag badges
     still popped their tooltips. pointer-events:none inherits to every
     child, so taps always fall through to the playback surface. */
  pointer-events: none;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  background: linear-gradient(
    180deg,
    rgba(0, 0, 0, 0.45) 0%,
    rgba(0, 0, 0, 0.65) 100%
  );
  ${({ $visible }) =>
    $visible &&
    css`
      animation: ${scrimSequence} 5s ease both;
    `}
`;

/**
 * Left-side stage: holds the faded team-logo silhouette behind the player
 * image. Both layers are positioned inside this box so the player photo
 * always sits on top of the crest no matter how the layout reflows.
 *
 * Sized to be only modestly taller than the meta column (vs pill +
 * headline + pills) and vertically centered against it — full-overlay
 * height was way too dominant.
 */
/* Outer wrapper handles vertical centering only (translateY). Inner does
   the slide-in / fade-out animation (translateX). They have to be on
   separate elements because a single `transform` property can't be both
   statically set AND animated — the animation always wins. */
const PlayerStage = styled(Box)<{ $bottomPlaylist?: boolean }>`
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  /* Sized for HLTV's in-page embed proportions. At small iframe widths
     the player cutout + headline pair was eating ~80% of the visible
     video; capped lower so the gameplay still reads through the intro. */
  height: 140px;
  max-height: 48%;
  width: 22%;

  /* Bottom-playlist variant: the intro group (player cutout + headline)
     is centered horizontally instead of left-anchored. PlayerStage
     slides off its left:0 anchor and sits at the left edge of a
     centered ~70%-wide composition; Content picks up the right side.
     The translate-X anchor pulls PlayerStage further left so there's
     visible breathing room between the cutout and the headline column
     — without the gap the player photo was kissing the VsPill, making
     the intro read as a single squished block. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    left: 50%;
    transform: translate(-190%, -50%);
    width: 18%;
  `}
`;

const PlayerStageInner = styled(Box)`
  position: relative;
  height: 100%;
  width: 100%;
  animation:
    ${slideInLeft} 1s ${SLIDE_EASE} both,
    ${fadeOut} 1s ease 4s both;
`;

/**
 * Team-crest silhouette: oversized + offset above the stage so its top
 * pokes above the player's head and its sides extend past the player's
 * shoulders, forming a faded halo behind the cutout. Sized larger than
 * the stage and positioned with negative top so the bottom of the crest
 * sits roughly mid-torso on the player image.
 */
const TeamSilhouette = styled(Box)<{ $src: string }>`
  position: absolute;
  top: -28%;
  left: -8%;
  right: -8%;
  height: 95%;
  background-image: url(${({ $src }) => $src});
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  opacity: 0.22;
  filter: grayscale(0.4) brightness(1.6) contrast(0.9);
  pointer-events: none;
`;

/**
 * Player image: anchored bottom-center of the stage, sized to fill the
 * stage height. Right-edge mask fades it into the dark scrim so the body
 * doesn't end with a hard rectangular crop.
 */
const PlayerCutout = styled(Box)<{ $src: string }>`
  position: absolute;
  inset: 0;
  background-image: url(${({ $src }) => $src});
  background-position: center bottom;
  background-repeat: no-repeat;
  background-size: auto 100%;
  -webkit-mask-image: linear-gradient(
    90deg,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 1) 60%,
    rgba(0, 0, 0, 0) 100%
  );
  mask-image: linear-gradient(
    90deg,
    rgba(0, 0, 0, 1) 0%,
    rgba(0, 0, 0, 1) 60%,
    rgba(0, 0, 0, 0) 100%
  );
`;

/* Same split-on-transform pattern as PlayerStage: outer holds the
   translateY centering, inner runs the slide/fade animations. */
const Content = styled(Box)<{ $bottomPlaylist?: boolean }>`
  left: 20%;
  max-width: 76%;
  position: absolute;
  right: 4%;
  top: 50%;
  transform: translateY(-50%);

  /* Bottom-playlist variant: Content drops its left/right anchors and
     instead sits at the iframe midline. Combined with PlayerStage's
     own shift this centers the [player cutout | headline group]
     composition horizontally — the whole intro reads as a centered
     takeover instead of a left-anchored callout. ContentInner keeps
     its slide-in animation; this wrapper only sets the static
     positioning. translateX offset is slightly more aggressive than
     50/50 split so a visible gap opens between the player cutout's
     right edge and the headline column's left edge. */
  ${({ $bottomPlaylist }) =>
    $bottomPlaylist &&
    `
    left: 50%;
    right: auto;
    max-width: 58%;
    transform: translate(-22%, -50%);
  `}
`;

const ContentInner = styled(Flex)`
  align-items: flex-start;
  flex-direction: column;
  gap: 8px;
  animation:
    ${slideInRight} 1s ${SLIDE_EASE} both,
    ${fadeOut} 1s ease 4s both;
`;

/**
 * Header pill: "[teamA] vs [teamB] | [eventLogo] {event}". Green outline +
 * dark fill, rounded ends. The vertical divider sits between the matchup
 * and the event so both clusters read as related-but-separate.
 */
const VsPill = styled(Flex)`
  align-items: center;
  background: rgba(12, 19, 24, 0.55);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid color-mix(in srgb, var(--accent, #3c87d2) 55%, transparent);
  border-radius: 999px;
  color: #fff;
  gap: 6px;
  padding: 4px 10px;
`;

const Divider = styled(Box)`
  background: rgba(255, 255, 255, 0.18);
  height: 12px;
  width: 1px;
`;

const VsLabel = styled(Text)`
  color: rgba(255, 255, 255, 0.7);
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.4px;
  text-transform: uppercase;
`;

const EventLogo = styled(Image)`
  height: 12px;
  object-fit: contain;
  width: 12px;
`;

const EventLabel = styled(Text)`
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.2px;
`;

const Headline = styled(Text)`
  color: #fff;
  /* Scaled down for HLTV's narrower in-page embed — at clamp(22, 3.6vw,
     36) the headline was eating the entire content column and pushing
     the tag pills off the bottom. */
  font-size: clamp(15px, 2.6vw, 24px);
  font-weight: 800;
  letter-spacing: -0.2px;
  line-height: 1.1;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.55);
`;

const PillsRow = styled(Flex)`
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
`;

const ViewsChip = styled(Flex)`
  align-items: center;
  color: rgba(255, 255, 255, 0.92);
  font-size: 10px;
  font-weight: 700;
  gap: 4px;
  letter-spacing: 0.2px;
  padding-left: 2px;

  & svg {
    color: var(--allstar-accent, #3c87d2);
  }
`;

const formatViews = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M views`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k views`;
  return `${n} views`;
};

export const IntroOverlay = () => {
  const phase = useRecoilValue(BrandedPlaybackPhaseState);
  const clip = useRecoilValue(BrandedActiveClipState);
  const layoutVariant = useRecoilValue(BrandedLayoutVariantState);
  const isBottomPlaylist = layoutVariant === "bottomPlaylist";
  const visible = phase === "intro";

  const playerPhoto = getPlayerPhoto(clip);
  const playerName = getPlayerName(clip);
  const teamName = getTeamName(clip);
  const teamLogo = getTeamLogo(clip);
  const eventName = getEventName(clip);
  const eventLogo = getEventLogo(clip);
  // Mock tags still — partner-api emits CS_Situational etc but the badge
  // mix here is curated against a specific design vocabulary; revisit
  // when getTagBadges is ready to drive the intro overlay directly.
  const tclipTags = buildMockTClipTags(clip);
  const views = getViewCount(clip);
  const title = composeBrandedTitle(clip);

  // Opponent team is now real (partner-api PR #1057). matchStage is
  // still mock — no field for it in the HLTV ingest pipeline yet, so
  // we keep the deterministic-per-clip fallback until partner-api adds
  // it. Mock context only computed when at least one mock field is
  // actually needed.
  const realOpponentName = getOpponentTeamName(clip);
  const realOpponentLogo = getOpponentTeamLogo(clip);
  const mock = buildMockMatchContext(clip);
  const opponentName = realOpponentName || mock.opponentTeamName;
  const opponentLogo = realOpponentLogo || mock.opponentTeamLogo;

  // Key on clip._id forces the Scrim to remount when the active clip
  // changes, restarting the CSS animations from frame 0. Without this,
  // styled-components reuses the same DOM node and the keyframes don't
  // re-trigger.
  return (
    <Scrim key={clip?._id ?? "no-clip"} $visible={visible}>
      <PlayerStage $bottomPlaylist={isBottomPlaylist}>
        <PlayerStageInner>
          {teamLogo && <TeamSilhouette $src={teamLogo} aria-hidden />}
          {playerPhoto && (
            <PlayerCutout $src={playerPhoto} aria-label={playerName} />
          )}
        </PlayerStageInner>
      </PlayerStage>
      <Content $bottomPlaylist={isBottomPlaylist}>
        <ContentInner>
          <VsPill>
            <TeamCrest name={teamName} logoUrl={teamLogo} size={14} />
            <VsLabel>vs</VsLabel>
            <TeamCrest name={opponentName} logoUrl={opponentLogo} size={14} />
            <Divider />
            {eventLogo && <EventLogo src={eventLogo} alt={eventName} />}
            <EventLabel>
              {eventName} {mock.matchStage}
            </EventLabel>
          </VsPill>
          <Headline>{title || `${playerName}'s highlight`}</Headline>
          <PillsRow>
            <ClipBadges tags={tclipTags} size="sm" maxVisible={3} branded />
            <ViewsChip>
              <TrendingIcon size={11} />
              <Box as="span">{formatViews(views)}</Box>
            </ViewsChip>
          </PillsRow>
        </ContentInner>
      </Content>
    </Scrim>
  );
};
